import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Card, Empty, List, Space, Spin, Switch, Tag, Typography, message } from "antd";

import { HabitCompletionStatus } from "@fitness-app/data-models/entities/Habit";

import { type SimulatorTabProps } from "../types";
import {
  DIRECTLY_COMPLETABLE_TASK_TYPES,
  listTraineeChallenges,
  taskScoringSource,
  toggleChallengeTaskCompletion,
  type SimulatorChallenge,
  type SimulatorChallengeTask,
} from "../writes/challengeWrites";
import { listTraineeHabits, toggleHabitCompletion, type SimulatorHabit } from "../writes/habitWrites";

const SimulatorHabitsTab = ({ target, date, createdBy, onWritten }: SimulatorTabProps) => {
  const queryClient = useQueryClient();
  const habitsKey = ["simulator", "habits", target.id, date];
  const challengesKey = ["simulator", "challenges", target.id, date];

  const habits = useQuery({
    queryKey: habitsKey,
    queryFn: () => listTraineeHabits(target.id, date),
  });

  const challenges = useQuery({
    queryKey: challengesKey,
    queryFn: () => listTraineeChallenges(target.id, date),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: habitsKey });
    void queryClient.invalidateQueries({ queryKey: challengesKey });
    onWritten();
  };

  const toggleHabit = useMutation({
    mutationFn: ({ habit, completed }: { habit: SimulatorHabit; completed: boolean }) =>
      toggleHabitCompletion({ habit, traineeId: target.id, date, createdBy, completed, mode: "single" }),
    onSuccess: refresh,
    onError: (error: Error) => void message.error(error.message),
  });

  const toggleTask = useMutation({
    mutationFn: ({
      challenge,
      task,
      completed,
    }: {
      challenge: SimulatorChallenge;
      task: SimulatorChallengeTask;
      completed: boolean;
    }) =>
      toggleChallengeTaskCompletion({
        challengeId: challenge.challengeId,
        participantId: challenge.participantId,
        task,
        traineeId: target.id,
        date,
        createdBy,
        completed,
        mode: "single",
      }),
    onSuccess: refresh,
    onError: (error: Error) => void message.error(error.message),
  });

  if (habits.isLoading || challenges.isLoading) {
    return <Spin />;
  }

  // Bez tego nieudany odczyt wygląda jak „brak nawyków i żadnego wyzwania" — komunikat nieprawdziwy
  // i zjadający treść błędu.
  if (habits.isError || challenges.isError) {
    return <Alert type="error" showIcon message={((habits.error ?? challenges.error) as Error).message} />;
  }

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Card size="small" title={`Nawyki na ${date}`}>
        {habits.data?.length ? (
          <List
            dataSource={habits.data}
            renderItem={(habit) => {
              const isDone = habit.dayStatus === HabitCompletionStatus.Completed;
              return (
                <List.Item
                  actions={[
                    <Switch
                      key="toggle"
                      checked={isDone}
                      loading={toggleHabit.isLoading}
                      onChange={(checked) => toggleHabit.mutate({ habit, completed: checked })}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={habit.name}
                    description={
                      habit.dayStatus === HabitCompletionStatus.Partial ? <Tag color="orange">częściowo</Tag> : null
                    }
                  />
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty description="Podopieczna nie ma aktywnych nawyków" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      {challenges.data?.length ? (
        challenges.data.map((challenge) => (
          <Card
            key={challenge.challengeId}
            size="small"
            title={
              <Space>
                <span>{challenge.name}</span>
                <Tag color="blue">{challenge.points ?? 0} pkt</Tag>
                {challenge.rank ? <Tag>miejsce {challenge.rank}</Tag> : null}
              </Space>
            }
          >
            <List
              dataSource={challenge.tasks}
              renderItem={(task) => {
                const canToggle = DIRECTLY_COMPLETABLE_TASK_TYPES.includes(task.type);
                const source = taskScoringSource(task.type);
                return (
                  <List.Item
                    actions={[
                      <Switch
                        key="toggle"
                        checked={Boolean(task.completionId)}
                        disabled={!canToggle}
                        loading={toggleTask.isLoading}
                        onChange={(checked) => toggleTask.mutate({ challenge, task, completed: checked })}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space size={8}>
                          <span>{task.title}</span>
                          <Tag>{task.points} pkt</Tag>
                        </Space>
                      }
                      description={
                        canToggle ? null : <Typography.Text type="secondary">Punktuje z: {source}</Typography.Text>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        ))
      ) : (
        <Alert
          type="info"
          showIcon
          message="Podopieczna nie bierze udziału w żadnym wyzwaniu"
          description="Dodaj ją do wyzwania w module Wyzwania, żeby móc odhaczać jego zadania."
        />
      )}
    </Space>
  );
};

export default SimulatorHabitsTab;
